import React from 'react'
import { cn } from 'cn'
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/solid'

export type NoticeVariant = 'info' | 'warn' | 'danger' | 'success'

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>

type Props = {
  title?: string | null
  message?: string | React.ReactElement
  variant: NoticeVariant
  className?: string
  size?: 'md' | 'lg'
  fullWidth?: boolean
  icon?: IconComponent
  accentBorder?: boolean
}

const variantConfig = {
  info: {
    border: 'border-blue-400',
    bg: 'bg-blue-50',
    iconColor: 'text-blue-400',
    titleColor: 'text-blue-800',
    Icon: InformationCircleIcon
  },
  warn: {
    border: 'border-yellow-400',
    bg: 'bg-yellow-50',
    iconColor: 'text-yellow-400',
    titleColor: 'text-yellow-800',
    Icon: ExclamationTriangleIcon
  },
  success: {
    border: 'border-green-400',
    bg: 'bg-green-50',
    iconColor: 'text-green-400',
    titleColor: 'text-green-800',
    Icon: CheckCircleIcon
  },
  danger: {
    border: 'border-red-400',
    bg: 'bg-red-50',
    iconColor: 'text-red-400',
    titleColor: 'text-red-800',
    Icon: XCircleIcon
  }
}

export default function NoticeBlock({
  message,
  title,
  variant,
  className,
  size = 'md',
  fullWidth = true,
  icon,
  accentBorder = true
}: Props) {
  const config = variantConfig[variant]
  const Icon = icon ?? config.Icon

  return (
    <div
      className={cn(
        'text-sm',
        config.bg,
        accentBorder
          ? ['rounded-r-lg', 'border-l-4', config.border]
          : 'rounded-lg',
        {
          'p-2 pl-4': size === 'md',
          'p-6': size === 'lg',
          'inline-flex pr-4': !fullWidth
        },
        className
      )}
    >
      <div className="flex items-center">
        <div className="shrink-0">
          <Icon className={cn('h-5 w-5', config.iconColor)} />
        </div>
        <div className="ml-3">
          {title && (
            <p
              className={cn('font-medium', config.titleColor, {
                'text-base': size === 'lg',
                'mb-2': message
              })}
            >
              {title}
            </p>
          )}
          {message && <div className="text-sm text-gray-700">{message}</div>}
        </div>
      </div>
    </div>
  )
}
